import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { X, Calendar, Loader2, Users, Building2, FileText, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const monthNames = [
  'Januari','Februari','Maret','April','Mei','Juni',
  'Juli','Agustus','September','Oktober','November','Desember',
];

/**
 * GenerateMonthlyModal — generate tagihan bulanan dengan filter scope:
 *  - Semua (kosongkan kedua filter)
 *  - Per Owner saja
 *  - Per Cabang saja
 *  - Kombinasi Owner + Cabang (AND filter)
 *
 * Props:
 *  - isOpen, onClose, onGenerated
 */
const GenerateMonthlyModal = ({ isOpen, onClose, onGenerated }) => {
  const { user } = useAuth();
  const userBranchId = user?.branch_id ? String(user.branch_id) : null;
  const isBranchScoped = ['kepala_cabang', 'teknisi', 'customer service'].includes(String(user?.role || '').toLowerCase()) && userBranchId;
  const [owners, setOwners] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const today = new Date();
  const currentMonthLabel = `${monthNames[today.getMonth()]} ${today.getFullYear()}`;

  // Compute scope summary
  const scopeSummary = (() => {
    const ownerName = owners.find((o) => String(o.id) === String(selectedOwnerId))?.name;
    const branchName = branches.find((b) => String(b.id) === String(selectedBranchId))?.name;
    if (!selectedOwnerId && !selectedBranchId) return { label: 'Semua Customer Aktif', color: 'emerald' };
    if (selectedOwnerId && selectedBranchId) return { label: `Owner: ${ownerName} + Cabang: ${branchName}`, color: 'indigo' };
    if (selectedOwnerId) return { label: `Owner: ${ownerName}`, color: 'indigo' };
    return { label: `Cabang: ${branchName}`, color: 'indigo' };
  })();

  useEffect(() => {
    if (!isOpen) return;
    setSelectedOwnerId('');
    setSelectedBranchId(isBranchScoped ? userBranchId : '');
    setResult(null);

    (async () => {
      setLoadingMeta(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [oRes, bRes] = await Promise.all([
          axios.get(`${API_URL}/api/v2/owners`, { params: { limit: 1000 }, headers }),
          axios.get(`${API_URL}/api/v2/branches`, { params: { limit: 1000 }, headers }),
        ]);
        setOwners((oRes.data?.data || []).filter((o) => !o.deleted_at));
        setBranches((bRes.data?.data || []).filter((b) => b.is_active !== 0));
      } catch (e) {
        // silent
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, [isOpen]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedOwnerId) params.owner_id = selectedOwnerId;
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const r = await axios.post(`${API_URL}/api/v2/invoices/generate-monthly`, null, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = r.data?.data;
      if (r.data?.success && data) {
        setResult(data);
        toast.success(`Generate ${data.generated_count} invoice berhasil. ${data.skipped_count} dilewati.`);
        onGenerated?.();
      } else {
        toast.error(r.data?.message || 'Gagal generate');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal generate invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedOwnerId('');
    setSelectedBranchId('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="generate-monthly-modal">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/40 rounded-md">
              <FileText className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Generate Tagihan Bulanan</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 flex items-start gap-2">
            <Calendar className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-amber-700 dark:text-amber-300">Periode: {currentMonthLabel}</div>
              <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                Customer yang sudah punya tagihan bulan ini akan dilewati otomatis.
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Filter Scope (kosongkan = semua)</span>
              </div>
              {(selectedOwnerId || selectedBranchId) && (
                <button onClick={handleReset} className="text-xs text-red-600 hover:underline" data-testid="reset-filters">
                  Reset
                </button>
              )}
            </div>

            <div className="space-y-3">
              {/* Owner dropdown */}
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Owner
                </label>
                <select
                  value={selectedOwnerId}
                  onChange={(e) => setSelectedOwnerId(e.target.value)}
                  disabled={loadingMeta}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 disabled:opacity-50"
                  data-testid="owner-select"
                >
                  <option value="">— Semua Owner —</option>
                  {owners.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} {o.code ? `(${o.code})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Branch dropdown */}
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Cabang
                </label>
                <select
                  value={selectedBranchId}
                  onChange={(e) => { if (isBranchScoped) return; setSelectedBranchId(e.target.value); }}
                  disabled={loadingMeta || isBranchScoped}
                  title={isBranchScoped ? 'Anda hanya dapat generate tagihan untuk cabang Anda sendiri' : undefined}
                  className={"w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md dark:bg-slate-800 disabled:opacity-50 " + (isBranchScoped ? "bg-slate-100 cursor-not-allowed" : "bg-white")}
                  data-testid="branch-select"
                >
                  {!isBranchScoped && <option value="">— Semua Cabang —</option>}
                  {branches.filter(b => !isBranchScoped || String(b.id) === userBranchId).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}{b.customer_count !== undefined ? ` (${b.customer_count} customer)` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Live scope preview */}
              <div className={`p-2.5 rounded-lg border text-xs ${
                scopeSummary.color === 'emerald'
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                  : 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300'
              }`} data-testid="scope-preview">
                <span className="font-medium">Akan generate untuk: </span>
                {scopeSummary.label}
              </div>
            </div>
          </div>

          {result && (
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-1">✓ Generate Selesai</div>
              <div className="text-xs space-y-0.5 text-emerald-600 dark:text-emerald-400">
                <div>• Berhasil dibuat: <strong>{result.generated_count}</strong> invoice</div>
                <div>• Dilewati (sudah ada): <strong>{result.skipped_count}</strong></div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            {result ? 'Tutup' : 'Batal'}
          </button>
          {!result && (
            <button
              onClick={handleSubmit}
              disabled={submitting || loadingMeta}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="generate-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Generate Sekarang
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateMonthlyModal;
